import {Component, Input, OnInit} from "@angular/core";
import {PropertyChangeListener} from "../../../../tools/form/form-field-config";
import {FormConfigBase} from "../../../../tools/form/form-config-base";
import {SelectionOption, SelectionOptions} from "../../../../tools/form/form-field.component";
import {EcgPreanalysisDataFormValidator} from "./ecg-preanalysis-data-form-validator";
import {ReleaseToggleState} from "../../../../tools/release-toggles/release-toggle-state";

@Component({
    selector: "app-ecg-preanalysis-data",
    templateUrl: "./ecg-preanalysis-data.component.html",
    standalone: false
})
export class EcgPreanalysisDataComponent implements OnInit, PropertyChangeListener<EcgPreanalysisFormModel> {

  @Input()
  formModel!: EcgPreanalysisFormModel;

  @Input()
  formValidator!: EcgPreanalysisDataFormValidator;

  config!: EcgPreanalysisFormFieldConfig;

  feature505Released = ReleaseToggleState
    .getInstance()
    .isReleased("TECHNICIAN_ADDS_ADDITIONAL_PQ_INTERVALS_505");

  ngOnInit(): void {
    this.config = new EcgPreanalysisFormFieldConfig(
      this.formModel,
      this,
      (property) => this.formValidator.isPropertyValid(property),
    );
  }

  handlePropertyChange(property: keyof EcgPreanalysisFormModel, newValue: any): void {
    (this.formModel as any)[property] = newValue;
    this.formValidator.updatePropertyValidation(property);

    if(property === "pqCannotMeasure") {
      this.formModel.pq2NotPresent = true;
      this.formModel.pq3NotPresent = true;
    }
  }

  addAdditionalPqInterval() {
    if (this.formModel.pq2NotPresent) {
      this.formModel.pq2NotPresent = false;
    } else {
      this.formModel.pq3NotPresent = false;
    }
  }

  removeAdditionalPqInterval() {
    if(!this.formModel.pq3NotPresent) {
      this.formModel.pq3NotPresent = true;
    } else {
      this.formModel.pq2NotPresent = true;
    }
  }
}

export interface EcgPreanalysisFormModel {
  avConduction?: AvConduction;

  pqInterval?: number;
  pqHeartRate?: number;

  qtInterval?: number;
  qtHeartRate?: number;

  qrsInterval?: number;
  qrsHeartRate?: number;

  pqCannotMeasure: boolean;
  qtCannotMeasure: boolean;
  qrsCannotMeasure: boolean;

  pqInterval2?: number;
  pqHeartRate2?: number;

  pqInterval3?: number;
  pqHeartRate3?: number;

  pq2NotPresent: boolean;
  pq3NotPresent: boolean;
}

export type AvConduction =
  "normal" |
  "_1degAVB" |
  "_2degAVB" |
  "_2degAvb1" |
  "_2degAvb2" |
  "_2_1Avb" |
  "_3degAvb" |
  "_otherAvb";

export const avConductionLabels: {
  [key in AvConduction]: string
} = {
  normal: "Normal",
  _1degAVB: "1° AVB",
  _2degAVB: "2° AVB",
  _2degAvb1: "2° AVB I",
  _2degAvb2: "2° AVB II",
  _2_1Avb: "2:1 AVB",
  _3degAvb: "3° AVB",
  _otherAvb: "Other AVB",
};

class EcgPreanalysisFormFieldConfig extends FormConfigBase<EcgPreanalysisFormModel> {

  private readonly avConductionOptions: SelectionOptions<AvConduction> = {
    isColumnLayout: false,
    items: ([
      "normal",
      "_1degAVB",
      "_2degAVB",
      "_2degAvb1",
      "_2degAvb2",
      "_2_1Avb",
      "_3degAvb"
    ] as AvConduction[])
      .map<SelectionOption<AvConduction>>(
        grade => (
          {value: grade, text: avConductionLabels[grade]}
        )
      )
  };

  readonly avConduction = this.createField({
    property: "avConduction",
    type: "dropdown",
    label: "preanalysisDataAvConductionLabel",
    invalidLabel: "preanalysisDataAvConductionInvalidLabel",
    selectionOptions: this.avConductionOptions,
  });

  readonly pqInterval = this.createField({
    property: "pqInterval",
    type: "number",
    label: "preanalysisDataIntervalLabel",
    invalidLabel: "preanalysisDataFieldInvalidLabel",
    infoLabel: "infoLabelMilliseconds"
  });

  readonly pqHeartRate = this.createField({
    property: "pqHeartRate",
    type: "number",
    label: "preanalysisDataHeartRateLabel",
    invalidLabel: "preanalysisDataFieldInvalidLabel",
    infoLabel: "infoLabelBeatsPerMinute"
  });

  readonly qtInterval = this.createField({
    property: "qtInterval",
    type: "number",
    label: "preanalysisDataIntervalLabel",
    invalidLabel: "preanalysisDataFieldInvalidLabel",
    infoLabel: "infoLabelMilliseconds"
  });

  readonly qtHeartRate = this.createField({
    property: "qtHeartRate",
    type: "number",
    label: "preanalysisDataHeartRateLabel",
    invalidLabel: "preanalysisDataFieldInvalidLabel",
    infoLabel: "infoLabelBeatsPerMinute"
  });

  readonly qrsInterval = this.createField({
    property: "qrsInterval",
    type: "number",
    label: "preanalysisDataIntervalLabel",
    invalidLabel: "preanalysisDataFieldInvalidLabel",
    infoLabel: "infoLabelMilliseconds"
  });

  readonly qrsHeartRate = this.createField({
    property: "qrsHeartRate",
    type: "number",
    label: "preanalysisDataHeartRateLabel",
    invalidLabel: "preanalysisDataFieldInvalidLabel",
    infoLabel: "infoLabelBeatsPerMinute"
  });

  readonly pqCannotMeasure = this.createField({
    property: "pqCannotMeasure",
    type: "checkbox",
    label: "preanalysisDataCannotMeasure",
  });
  readonly qtCannotMeasure = this.createField({
    property: "qtCannotMeasure",
    type: "checkbox",
    label: "preanalysisDataCannotMeasure",
  });
  readonly qrsCannotMeasure = this.createField({
    property: "qrsCannotMeasure",
    type: "checkbox",
    label: "preanalysisDataCannotMeasure",
  });


  readonly pqInterval2 = this.createField({
    property: "pqInterval2",
    type: "number",
    label: "preanalysisDataIntervalLabel",
    invalidLabel: "preanalysisDataFieldInvalidLabel",
    infoLabel: "infoLabelMilliseconds"
  });

  readonly pqHeartRate2 = this.createField({
    property: "pqHeartRate2",
    type: "number",
    label: "preanalysisDataHeartRateLabel",
    invalidLabel: "preanalysisDataFieldInvalidLabel",
    infoLabel: "infoLabelBeatsPerMinute"
  });

  readonly pqInterval3 = this.createField({
    property: "pqInterval3",
    type: "number",
    label: "preanalysisDataIntervalLabel",
    invalidLabel: "preanalysisDataFieldInvalidLabel",
    infoLabel: "infoLabelMilliseconds"
  });

  readonly pqHeartRate3 = this.createField({
    property: "pqHeartRate3",
    type: "number",
    label: "preanalysisDataHeartRateLabel",
    invalidLabel: "preanalysisDataFieldInvalidLabel",
    infoLabel: "infoLabelBeatsPerMinute"
  });
}
